import {
  BoundsCalculator,
  DefaultCamera,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { BasicConeSurface3D } from 'features/assetViz/objects/surfaces/basic/BasicConeSurface3D'
import { BasicPlaneSurface3D } from 'features/assetViz/objects/surfaces/basic/BasicPlaneSurface3D'
import {
  AllSceneFactoryProps,
  SceneFactoryConfig,
} from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurementUnit,
} from 'types'

export const CdfFallbackSceneConfig: SceneFactoryConfig = {
  name: 'CDF Fallback',
  otherChecks: (props: AllSceneFactoryProps) => !!props.cdf,
  fullSceneFunc: (props: AllSceneFactoryProps) =>
    !!props.cdf && <CdfFallbackScene cdf={props.cdf} />,
}

/**
 * A scene which renders a CDF in 2D space without a corresponding asset-model.
 * Handles both Cartesian and polar coordinate systems.
 */
export const CdfFallbackScene: React.FC<{
  cdf: GeckoComponentDataFrame
}> = ({ cdf }) => {
  //----------------------------------------------------------------------------

  const { cdfGeometry, usesPolarCoordinates } = React.useMemo(() => {
    if (cdf.data.geometry.x.unit === GeckoMeasurementUnit.Degrees) {
      return {
        cdfGeometry: GeckoComponentRect.CreateFromXYs(
          cdf.data.geometry.componentID,
          -cdf.data.geometry.end.y.num,
          -cdf.data.geometry.end.y.num,
          cdf.data.geometry.end.y.num,
          cdf.data.geometry.end.y.num,
          cdf.data.geometry.y.unit,
          cdf.data.geometry.y.unit
        ),
        usesPolarCoordinates: true,
      }
    }
    return {
      cdfGeometry: cdf?.data.geometry,
      usesPolarCoordinates: false,
    }
  }, [cdf])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () => cdfGeometry,
    [cdfGeometry]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(() => {
    return {
      rect: cdfGeometry,
    }
  }, [cdfGeometry])

  const getCameraPosition = React.useCallback<PositionCalculator>(
    (coords) => {
      if (usesPolarCoordinates)
        return {
          position: [
            coords.componentX,
            coords.componentY,
            coords.distToComponent,
          ],
          rotation: [0, 0, 0],
        }

      const xStart = cdfGeometry.start.x.num
      const yStart = cdfGeometry.start.y.num
      const x = coords.componentX - xStart
      const y = coords.componentY - yStart
      const z = coords.distToComponent
      return { position: [x, y, z], rotation: [0, 0, 0] }
    },
    [usesPolarCoordinates, cdfGeometry.start.x, cdfGeometry.start.y]
  )

  //----------------------------------------------------------------------------

  return (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
      />
      {usesPolarCoordinates ? <BasicConeSurface3D /> : <BasicPlaneSurface3D />}
    </>
  )
}
